import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import type { FormikProps } from 'formik'
import styled from 'styled-components'
import { Accordion } from 'sutra/components/Accordion/Accordion'
import { Button } from 'sutra/components/Button'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Divider } from 'sutra/components/Divider/Divider'
import { Form, FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Select } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { FormikFormItem, Loader } from 'components'
import type { SharedUsersResponse } from 'features/share/types'

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
}

interface ShareDashboardViewProps {
  sharedUsers: SharedUsersResponse | undefined
  isLoading: boolean
  formik: FormikProps<{ role: string; email: string }>
  isAdmin: boolean
  isUserAdmin: boolean
  userEmail: string | undefined
  errorMessage: string
  shareUrlExpanded: string
  isSharingUrlLoading: boolean
  isLocalLoading: boolean
  onCopyToClipboard: () => void
  onEnableSharing: () => void
  onDisableSharing: () => void
  onRemoveEmail: (email: string) => void
  onFormSubmit: () => void
}

export const ShareDashboardView = ({
  sharedUsers,
  isLoading,
  formik,
  isAdmin,
  isUserAdmin,
  userEmail,
  errorMessage,
  shareUrlExpanded,
  isSharingUrlLoading,
  isLocalLoading,
  onCopyToClipboard,
  onEnableSharing,
  onDisableSharing,
  onRemoveEmail,
  onFormSubmit,
}: ShareDashboardViewProps): JSX.Element => {
  if (isLoading || !sharedUsers) {
    return <Loader />
  }

  return (
    <Wrapper>
      <Form
        layout="horizontal"
        className="share-form"
        onFinish={onFormSubmit}
        {...layout}
      >
        <FlexRow style={{ gap: 20, marginBottom: 16 }}>
          <FormikFormItem
            label="Email"
            field="email"
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            style={{ marginBottom: 0, width: '100%' }}
          />
          {isUserAdmin && (
            <Select
              value={formik.values.role}
              style={{ width: 100 }}
              onChange={(v) => formik.setFieldValue('role', v)}
              options={[
                { value: 'viewer', label: 'User' },
                ...(isUserAdmin ? [{ value: 'admin', label: 'Admin' }] : []),
              ]}
            />
          )}
        </FlexRow>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
          <Button variant="primary" type="submit" loading={formik.isSubmitting}>
            Share
          </Button>
        </div>
        {errorMessage && (
          <p style={{ color: 'red', textAlign: 'center' }}>{errorMessage}</p>
        )}
        <Divider />
        {isUserAdmin && (
          <>
            <Accordion
              items={[
                {
                  value: 'admins',
                  header: 'Admins',
                  children: (
                    <FormItem label="Admins">
                      <Users>
                        {sharedUsers.admins
                          .concat(sharedUsers.yogi_employees)
                          .map((item) => (
                            <Chiclet key={item}>{item}</Chiclet>
                          ))}
                      </Users>
                    </FormItem>
                  ),
                },
              ]}
            />
            <div style={{ height: 20 }}></div>
          </>
        )}
        <Accordion
          items={[
            {
              value: 'users',
              header: 'Users',
              children: (
                <FormItem label="Users">
                  <Users>
                    {sharedUsers.shared_users.length
                      ? sharedUsers.shared_users.map((item) => (
                          <Chiclet
                            key={item}
                            onRemove={
                              isUserAdmin && item !== userEmail
                                ? () => onRemoveEmail(item)
                                : undefined
                            }
                            removeLabel={`Remove ${item}`}
                          >
                            {item}
                          </Chiclet>
                        ))
                      : 'No users...'}
                  </Users>
                </FormItem>
              ),
            },
          ]}
        />
        {isAdmin && (
          <>
            <Divider />
            <FormItem label="Public URL">
              <Input
                value={shareUrlExpanded}
                style={{ width: 200, marginRight: 20 }}
                disabled
              />
              <CopyButton
                variant="primary"
                icon={<CopyOutlined />}
                aria-label="Copy public URL"
                onClick={onCopyToClipboard}
                disabled={isSharingUrlLoading}
              />
              <Button variant="secondary" onClick={onEnableSharing}>
                On
              </Button>
              <Button variant="secondary" onClick={onDisableSharing}>
                Off
              </Button>
              <Tooltip content="Toggle public sharing of this project. If enabled, you can share this URL with people, and they can access this project without a Yogi login.">
                <InfoCircleOutlined style={{ marginLeft: 12 }} />
              </Tooltip>
            </FormItem>
          </>
        )}
      </Form>
      {isLocalLoading && (
        <LoadingLayer>
          <Loader />
        </LoadingLayer>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
`

const CopyButton = styled(Button)`
  margin-right: 20px;
  border-radius: 50%;
`

const Users = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 8px;

  & > * {
    margin-bottom: 6px;
  }
`

const LoadingLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.6);
`

const FlexRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`
